import { useMutation } from 'react-query'
import { useEnvVars } from './useEnvVars'
import { Coordinates } from '../types/Order'

/**
 * Distinguishes a dead link (bad/expired token, or a resolution that no longer
 * exists) from every other failure, so the UI can show a dedicated "link
 * expired" terminal state rather than a generic retryable error.
 */
export type ConfirmPinErrorKind = 'expired' | 'error'

/**
 * Statuses from geocoding's confirm-link that mean the LINK itself is dead and
 * retrying won't help → terminal "expired" state. Verified against geocoding
 * PR #52 (`internal/resolution/routes.go` `confirmLinkHandler`): a bad/expired
 * token is 400 and a missing resolution is 404 (401 is included defensively).
 * Everything else — timeouts (408), rate limiting (429), 5xx, transport — is a
 * transient failure and must stay a retryable 'error', NOT be mislabelled as an
 * expired link.
 */
const LINK_DEAD_STATUSES = new Set([400, 401, 404])

/**
 * Error thrown by the confirm-link mutation. `kind` drives which terminal state
 * the pin-confirm page renders.
 */
export class ConfirmPinError extends Error {
  kind: ConfirmPinErrorKind
  status?: number

  constructor(kind: ConfirmPinErrorKind, message: string, status?: number) {
    super(message)
    this.name = 'ConfirmPinError'
    this.kind = kind
    this.status = status
  }
}

/**
 * Structured address the customer types on step 2 of the pin flow, in UI terms.
 * Every field is optional *to this hook* — the confirm-link contract accepts a
 * coordinates-only body — but the pin form requires all four before it submits
 * (sc-27850), so the blank-dropping below is defence in depth, not a path the
 * customer can take. The wire mapping (UI label -> geocoding field name) is
 * applied in the mutation, NOT here — see `buildAddressPayload`.
 */
export type ConfirmPinAddress = {
  /** UI "Building name" -> wire `address.floor` */
  buildingName?: string
  /** UI "Area" -> wire `address.area` */
  area?: string
  /** UI "Emirate" -> wire `address.state` */
  emirate?: string
  /** UI "Apartment / villa number" -> wire `address.flat` */
  apartmentNumber?: string
}

type ConfirmPinVariables = {
  token: string
  coordinates: Coordinates
  address?: ConfirmPinAddress
}

/**
 * Wire shape of the optional `address` block accepted by confirm-link. Field
 * names are geocoding's, NOT the customer-facing labels — see the mapping notes
 * on `ConfirmPinAddress`.
 */
type ConfirmPinAddressPayload = {
  area?: string
  state?: string
  floor?: string
  flat?: string
}

/**
 * Maps the UI address (labels the customer sees) onto geocoding's wire field
 * names, trims values, and drops any the customer left blank. Returns
 * `undefined` when nothing was filled so the request body omits `address`
 * entirely (matching the coordinates-only contract).
 *
 * Mapping (agreed with the geocoding backend, story sc-26879):
 *   Building name          -> floor
 *   Area                   -> area
 *   Emirate                -> state
 *   Apartment / villa no.  -> flat
 */
function buildAddressPayload(
  address?: ConfirmPinAddress
): ConfirmPinAddressPayload | undefined {
  if (!address) {
    return undefined
  }

  const payload: ConfirmPinAddressPayload = {}
  const area = address.area?.trim()
  const state = address.emirate?.trim()
  const floor = address.buildingName?.trim()
  const flat = address.apartmentNumber?.trim()

  if (area) {
    payload.area = area
  }
  if (state) {
    payload.state = state
  }
  if (floor) {
    payload.floor = floor
  }
  if (flat) {
    payload.flat = flat
  }

  return Object.keys(payload).length > 0 ? payload : undefined
}

/**
 * Posts the customer-confirmed delivery coordinates to the geocoding service.
 *
 * Contract (R047, story sc-26879):
 *   POST {GEOCODING_URL}/v1/resolutions/confirm-link
 *   body: { token, coordinates: { lat, lng }, address?: { area, state, floor, flat } }
 *   200 -> resolved & case closed; 4xx -> invalid/expired token.
 *
 * `address` is optional on the wire and only carries the keys it was given
 * (Phase 1 manual capture, Option B); given nothing it is omitted entirely and
 * the request is coordinates-only, exactly as before. The pin form itself no
 * longer submits a partial address — see `ConfirmPinAddress`.
 *
 * The `token` is an opaque signed string carried straight from the URL path;
 * it is never decoded or validated client-side. Auth lives entirely in that
 * token, so the request is unauthenticated (no Authorization header).
 *
 * Uses the raw `fetch` API rather than this app's `ApiClient` wrapper by
 * design: this is the only cross-origin, unauthenticated call to a non-core
 * host (the geocoding service, not the Quiqup core API). `ApiClient` injects
 * core-API headers (`Api-Version`, `agent`, `X-TimeOffset`, no-cache) on every
 * request, which are meaningless to geocoding and would force its CORS
 * preflight to allowlist all of them. Keeping the header footprint to just
 * `Content-Type` keeps that public endpoint's CORS trivial. We still read the
 * HTTP status directly to drive the expired-vs-retryable state machine.
 */
export function useConfirmPinLocation() {
  const { env } = useEnvVars()

  return useMutation<void, ConfirmPinError, ConfirmPinVariables>({
    mutationFn: async ({ token, coordinates, address }) => {
      const baseUrl = env.GEOCODING_URL

      if (!baseUrl) {
        throw new ConfirmPinError(
          'error',
          'Geocoding service URL is not configured'
        )
      }

      const addressPayload = buildAddressPayload(address)
      const body = {
        token,
        coordinates: { lat: coordinates.lat, lng: coordinates.lng },
        ...(addressPayload ? { address: addressPayload } : {}),
      }

      let response: Response

      try {
        response = await fetch(`${baseUrl}/v1/resolutions/confirm-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      } catch (e) {
        throw new ConfirmPinError('error', 'Network request failed')
      }

      if (response.ok) {
        return
      }

      if (LINK_DEAD_STATUSES.has(response.status)) {
        throw new ConfirmPinError(
          'expired',
          'This link is invalid or has expired',
          response.status
        )
      }

      throw new ConfirmPinError(
        'error',
        'Unexpected server error',
        response.status
      )
    },
  })
}
